import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchWidget from '../components/SearchWidget';
import { DestCard, BrandCardDark } from '../components/Cards';
import { SectionHead } from '../components/UI';
import { Icon } from '../components/Icons';
import { BRANDS, HOLIDAY_TYPES} from '../data';
import { usePackages } from '../hooks/usePackages';
import HappyClients from '../components/HappyClients';
import MaldivesImg from '../assets/images/view-breathtaking-beach-nature-landscape.jpg';
import UniverseBg from '../assets/images/flight.jpg';



// ── Trust strip data ─────────────────────────────────────────────────
const TRUST = [
  { val: '4.2L+', label: 'Travellers',   icon: 'users'    },
  { val: '190+',  label: 'Destinations', icon: 'globe'    },
  { val: '18 Yrs',label: 'Excellence',   icon: 'shield'   },
  { val: '4.9★',  label: 'Rating',       icon: 'star'     },
  { val: '24/7',  label: 'Support',      icon: 'support'  },
];

function TrustItem({ t }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'default' }}
    >
      <div style={{
        width: '34px', height: '34px',
        background: hovered ? 'rgba(255,182,0,0.1)' : 'rgba(2,2,191,0.15)',
        borderRadius: '9px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.3s',
        border: hovered ? '1px solid var(--secondary)' : '1px solid transparent'
      }}>
        <Icon name={t.icon} size={16} color={hovered ? 'var(--secondary)' : '#ffb600'} />
      </div>
      <div>
        <div style={{ fontSize: '22px', fontWeight: 800, color: hovered ? 'var(--secondary)' : '#fff', lineHeight: 1, transition: 'color 0.3s' }}>{t.val}</div>
        <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', textTransform: 'uppercase' }}>{t.label}</div>
      </div>
    </div>
  );
}

// ── Holiday Type Card ─────────────────────────────────────────────────
function TypeCard({ type, onNavigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="type-card"
      onClick={() => onNavigate(type.page, type.filterKey)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={type.special ? {
        background: hovered ? 'rgba(255,182,0,0.1)' : '#1c2230',
        borderColor: hovered ? 'var(--secondary)' : 'rgba(255,182,0,0.35)',
      } : {}}
    >
      <div style={{
        width: '50px', height: '50px',
        background: type.special ? 'rgba(255,182,0,0.12)' : 'var(--brand-lt)',
        borderRadius: '13px', margin: '0 auto 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <TypeIcon id={type.label} special={type.special} hovered={hovered} />
      </div>
      <div style={{
        fontSize: '13px',
        fontWeight: 700,
        color: type.special ? (hovered ? 'var(--brand)' : '#fff') : (hovered ? 'var(--brand)' : '#0d1117'),
        marginBottom: '4px',
        transition: 'color 0.3s'
      }}>
        {type.label}
      </div>
      <div style={{
        fontSize: '11px',
        color: type.special ? (hovered ? 'var(--brand)' : 'rgba(255,255,255,0.5)') : '#6b7a8f',
        transition: 'color 0.3s'
      }}>
        {type.count}
      </div>
    </div>
  );
}

// Inline SVG for each type card
function TypeIcon({ id, special, hovered }) {
  const color = hovered || special ? 'var(--secondary)' : 'var(--brand)';
  const s = { fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', style: { transition: 'stroke 0.3s' } };
  if (id.includes('Beach'))    return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M17 13L7 3"/><path d="M10.5 3.5c3.5 3.5 4 8 1 11s-7.5 2.5-11-1"/><path d="M2 22l10-10"/><path d="M17 22v-7"/><path d="M13 22h8"/></svg>;
  if (id.includes('Mountain'))  return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M3 20l7-12 4 6 2-3 5 9H3z"/><circle cx="18" cy="6" r="2"/></svg>;
  if (id.includes('City'))      return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><line x1="3" y1="22" x2="21" y2="22"/><rect x="2" y="10" width="8" height="12"/><rect x="14" y="14" width="8" height="8"/><path d="M6 10V6l6-4 6 4v4"/></svg>;
  if (id.includes('Family'))    return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  if (id.includes('Honeymoon')) return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
  if (id.includes('Adventure')) return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M4 20V10a8 8 0 0 1 16 0v10"/><path d="M9 20h6"/><path d="M12 2v4"/><path d="M8 10h8"/></svg>;
  if (id.includes('Luxury'))    return <svg width="24" height="24" viewBox="0 0 24 24" {...s}><path d="M12 22c-4-4-6-8-6-12a6 6 0 0 1 12 0c0 4-2 8-6 12z"/><path d="M12 10c0-2 2-4 5-5-1 3-2 5-5 5z"/><path d="M12 10c0-2-2-4-5-5 1 3 2 5 5 5z"/></svg>;
  // Blind Date dice
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" style={{ transition: 'stroke 0.3s' }}><rect x="2" y="2" width="20" height="20" rx="4"/><circle cx="8" cy="8" r="1.5" fill={color} style={{ transition: 'fill 0.3s' }}/><circle cx="16" cy="8" r="1.5" fill={color} style={{ transition: 'fill 0.3s' }}/><circle cx="12" cy="12" r="1.5" fill={color} style={{ transition: 'fill 0.3s' }}/><circle cx="8" cy="16" r="1.5" fill={color} style={{ transition: 'fill 0.3s' }}/><circle cx="16" cy="16" r="1.5" fill={color} style={{ transition: 'fill 0.3s' }}/></svg>;
}

// ── Testimonial Card ──────────────────────────────────────────────────
// function TestiCard({ t }) {
//   return (
//     <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '28px', border: '1.5px solid #e2e8f0' }}>
//       <Stars rating={t.rating} size={14} />
//       <p style={{ fontSize: '13px', color: '#3d4a5c', lineHeight: 1.8, margin: '14px 0 20px', fontStyle: 'italic' }}>{t.text}</p>
//       <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
//         <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#e8fbed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#1f9e3a' }}>
//           {t.name[0]}
//         </div>
//         <div>
//           <div style={{ fontSize: '13px', fontWeight: 700, color: '#0d1117' }}>{t.name}</div>
//           <div style={{ fontSize: '11px', color: '#6b7a8f' }}>{t.city} · <span style={{ color: '#1f9e3a' }}>{t.brand}</span></div>
//         </div>
//       </div>
//     </div>
//   );
// }

// ── Home Page ─────────────────────────────────────────────────────────
export default function Home() {
  const navigate = useNavigate();
  const { indiaDest, longHaulDest, cityDest } = usePackages();
  return (
    <div className="page-enter">
      {/* linear-gradient(135deg,#0d1117 0%,#0202bf20 55%,#0202bf40 100%) */}
      {/* HERO */}
      <section
        style={{
          background: '#ffffff ',
          padding: '56px 32px 0',
          minHeight: '92vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background patterns */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 80%,rgba(2,2,191,.09),transparent 50%),radial-gradient(circle at 85% 20%,rgba(255,182,0,.06),transparent 50%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(90deg,rgba(2,2,191,.025) 0,rgba(2,2,191,.025) 1px,transparent 1px,transparent 72px),repeating-linear-gradient(0deg,rgba(2,2,191,.025) 0,rgba(2,2,191,.025) 1px,transparent 1px,transparent 72px)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: '960px',
            textAlign: 'center',
          }}
        >
          {/* Banner image */}
          <div
            style={{
              marginBottom: '28px',
              borderRadius: '24px',
              overflow: 'hidden',
              border: '1.5px solid rgba(2,2,191,0.2)',
              position: 'relative',
              background: `url(${MaldivesImg}) center/cover no-repeat`,
              height: '340px',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top,rgba(2,2,191,0.8) 0%,transparent 70%)',
              }}
            />
            <div
              style={{
                position: 'relative',
                zIndex: 2,
                padding: '30px 36px',
                textAlign: 'left',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'rgba(255,182,0,0.15)',
                  border: '1px solid rgba(255,182,0,0.3)',
                  borderRadius: '20px',
                  padding: '5px 16px',
                  marginBottom: '14px',
                  backdropFilter: 'blur(4px)',
                }}
              >
                <div
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#ffb600',
                  }}
                />
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    letterSpacing: '2.5px',
                    color: '#ffb600',
                  }}
                >
                  INDIA'S MOST TRUSTED TRAVEL BRAND
                </span>
              </div>
              <h1
                style={{
                  fontSize: 'clamp(28px,5vw,56px)',
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1.1,
                  margin: '0 0 10px',
                  textShadow: '0 2px 24px rgba(0,0,0,0.5)',
                }}
              >
                Every Journey Is a Story
                <br />
                Waiting to Be Written
              </h1>
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.8)',
                  margin: 0,
                }}
              >
                190+ destinations · 12 travel universes · 18 years of excellence
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div
            style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '40px',
            }}
          >
            <button
              onClick={() => navigate('/holidays')}
              style={{
                padding: '16px 40px',
                background: '#ffb600',
                border: 'none',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 800,
                color: '#0d1117',
                boxShadow: '0 8px 20px rgba(255,182,0,0.3)',
              }}
            >
              Explore Packages
            </button>
            <button
              onClick={() => navigate('/experiences')}
              style={{
                // rgba(255,255,255,0.05)
                padding: '16px 40px',
                background: '#0202bf',
                border: '1.5px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                fontSize: '15px',
                fontWeight: 700,
                color: '#ffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backdropFilter: 'blur(10px)',
              }}
            >
               Blind Date Trip
            </button>
          </div>

          <SearchWidget />
        </div>
      </section>
      {/* TRUST STRIP */}
      <section
        style={{
          background: '#0d1117',
          padding: '24px 32px',
          borderBottom: '1px solid rgba(2,2,191,0.1)',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-around',
            flexWrap: 'wrap',
            gap: '14px',
          }}
        >
          {TRUST.map((t) => (
            <TrustItem key={t.val} t={t} />
          ))}
        </div>
      </section>
      {/* POPULAR HOLIDAY TYPES */}
      <section style={{ padding: '68px 32px', background: '#fff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <SectionHead
            eyebrow="Browse by Type"
            title="Popular Holiday Types"
            subtitle="Every travel style, every pace, every budget — choose what speaks to you."
          />
          <div className="responsive-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px' }}>
            {HOLIDAY_TYPES.map((t) => (
              <TypeCard key={t.label} type={t} onNavigate={(page) => navigate(`/${page}`)} />
            ))}
          </div>
        </div>
      </section>
      {/* POPULAR HOLIDAY DESTINATIONS (India) */}
      <section style={{ padding: '68px 32px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '40px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}
              >
                <span
                  style={{
                    width: '18px',
                    height: '2px',
                    background: 'var(--brand)',
                    display: 'inline-block',
                  }}
                />
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    letterSpacing: '2.5px',
                    color: 'var(--brand)',
                    textTransform: 'uppercase',
                  }}
                >
                  Discover India
                </span>
              </div>
              <h2 style={{ fontSize: 'clamp(20px,3vw,30px)', fontWeight: 800, color: '#0d1117' }}>
                Popular Holiday Destinations
              </h2>
            </div>
            <button
              onClick={() => navigate('/destinations')}
              style={{
                padding: '10px 22px',
                background: 'transparent',
                border: '2px solid var(--brand)',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 700,
                color: 'var(--brand)',
              }}
            >
              View All India →
            </button>
          </div>
          <div className="responsive-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px' }}>
            {indiaDest.map((d) => (
              <DestCard key={d.name} {...d} onClick={() => navigate(`/package/${d.name}`)} />
            ))}
          </div>
        </div>
      </section>
      {/* POPULAR LONG HAUL */}
      {longHaulDest.length > 0 && (
        <section style={{ padding: '68px 32px', background: '#fff' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '40px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}
                >
                  <span
                    style={{
                      width: '18px',
                      height: '2px',
                      background: 'var(--brand)',
                      display: 'inline-block',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 800,
                      letterSpacing: '2.5px',
                      color: 'var(--brand)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Fly Far
                  </span>
                </div>
                <h2 style={{ fontSize: 'clamp(20px,3vw,30px)', fontWeight: 800, color: '#0d1117' }}>
                  Popular Long Haul Destinations
                </h2>
              </div>
              <button
                onClick={() => navigate('/destinations')}
                style={{
                  padding: '10px 22px',
                  background: 'transparent',
                  border: '2px solid var(--brand)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: 'var(--brand)',
                }}
              >
                View All →
              </button>
            </div>
            <div className="responsive-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px' }}>
              {longHaulDest.map((d) => (
                <DestCard key={d.name} {...d} onClick={() => navigate(`/package/${d.name}`)} />
              ))}
            </div>
          </div>
        </section>
      )}
      {/* POPULAR CITY DESTINATIONS */}
      {cityDest.length > 0 && (
        <section style={{ padding: '68px 32px', background: '#f8fafc' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '40px',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              <div>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}
                >
                  <span
                    style={{
                      width: '18px',
                      height: '2px',
                      background: 'var(--brand)',
                      display: 'inline-block',
                    }}
                  />
                  <span
                    style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '2.5px',
                      color: 'var(--brand)',
                      textTransform: 'uppercase',
                    }}
                  >
                    Urban Explorer
                  </span>
                </div>
                <h2 style={{ fontSize: 'clamp(20px,3vw,30px)', fontWeight: 800, color: '#0d1117' }}>
                  Popular City Destinations
                </h2>
              </div>
              <button
                onClick={() => navigate('/destinations')}
                style={{
                  padding: '10px 22px',
                  background: 'transparent',
                  border: '2px solid var(--brand)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: 'var(--brand)',
                }}
              >
                View All Cities →
              </button>
            </div>
            <div className="responsive-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px' }}>
              {cityDest.map((d) => (
                <DestCard
                  key={d.city}
                  {...d}
                  name={d.city}
                  onClick={() => navigate(`/package/${d.city}`)}
                />
              ))}
            </div>
          </div>
        </section>
      )}
      {/* TRAVEL UNIVERSES */}
      <section
        style={{
          padding: '80px 32px',
          background: `linear-gradient(rgba(13, 17, 23, 0.85), rgba(13, 17, 23, 0.85)), url(${UniverseBg}) center/cover no-repeat fixed`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <SectionHead
            eyebrow="Travel Universes"
            title={<span style={{ color: '#fff' }}>Choose Your Journey Style</span>}
            subtitle={
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>
                12 curated travel brands — every kind of traveller finds their home with DIRA HOLIDAYS.
              </span>
            }
          />
          <div className="responsive-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px' }}>
            {BRANDS.map((b) => (
              <BrandCardDark key={b.id} brand={b} onClick={(page) => navigate(`/${page}`)} />
            ))}
          </div>
        </div>
      </section>
      {/* HAPPY CLIENTS CAROUSEL */}
      <HappyClients />
      {/* NEWSLETTER */}
      <section
        style={{
          padding: '80px 32px',
          background: 'var(--brand)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 10% 20%, rgba(255,182,0,0.15), transparent 40%)',
          }}
        />
        <div
          style={{
            maxWidth: '580px',
            margin: '0 auto',
            textAlign: 'center',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <h2 style={{ fontSize: '32px', fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
            Get Exclusive Deals
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.7)', marginBottom: '32px' }}>
            Join 2.3 lakh+ subscribers — flash sales, destination guides & curated itineraries.
          </p>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              maxWidth: '460px',
              margin: '0 auto',
              background: 'rgba(255,255,255,0.1)',
              padding: '6px',
              borderRadius: '12px',
              border: '1.5px solid rgba(255,255,255,0.1)',
            }}
          >
            <input
              placeholder="Your email address"
              style={{
                flex: 1,
                padding: '13px 18px',
                borderRadius: '8px',
                border: 'none',
                fontSize: '14px',
                color: '#fff',
                background: 'transparent',
              }}
            />
            <button
              style={{
                padding: '13px 28px',
                background: '#ffb600',
                color: '#0d1117',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 800,
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
